/**
 * Auth Guard Component
 * Protects routes and shows loading/error states during authentication.
 * Redirects unauthenticated users to the dashboard OAuth authorize endpoint.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { AuthError, generateOAuthState, storeReturnToPath } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { getDashboardUrl } from '@/components/theme-provider';

interface AuthGuardProps {
    children: React.ReactNode;
    fallback?: React.ReactNode;
    onUnauthenticated?: () => void;
}

function redirectToAuthorize() {
    const state = generateOAuthState();
    // Remember where the user was headed so the post-exchange init can restore it —
    // redirect_uri only carries the origin.
    storeReturnToPath(window.location.pathname + window.location.search);
    const params = new URLSearchParams({
        response_type: 'code',
        client_id: 'menteo-agent',
        redirect_uri: window.location.origin,
        state,
    });
    // Replace instead of push: the unauthenticated agent entry is useless in history
    // (going back to it would just restart the authorize redirect).
    window.location.replace(`${getDashboardUrl()}/oauth/authorize?${params.toString()}`);
}

/**
 * Access denied screen: shown when the SSO exchange is forbidden (403) or when
 * the session's identity has no operable WhatsApp line (agent_access === false).
 */
function AccessDeniedScreen() {
    const { t } = useTranslation();
    const dashboardUrl = getDashboardUrl();

    return (
        <div className="h-dvh flex items-center justify-center bg-muted/50">
            <div className="max-w-md w-full mx-4">
                <div className="bg-card rounded-lg shadow-lg p-6 text-center">
                    <div className="h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center mx-auto mb-4">
                        <svg
                            className="h-8 w-8 text-red-600 dark:text-red-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                            />
                        </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">
                        {t('authGuard.accessDenied.title')}
                    </h2>
                    <p className="text-muted-foreground mb-6">
                        {t('authGuard.accessDenied.description')}
                    </p>
                    {dashboardUrl && (
                        <Button asChild className="w-full mb-2">
                            <a href={dashboardUrl}>{t('authGuard.accessDenied.goToDashboard')}</a>
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

export function AuthGuard({ children, fallback, onUnauthenticated }: AuthGuardProps) {
    const { t } = useTranslation();
    const { session, loading, ready, error } = useAuth();
    const redirectedRef = useRef(false);
    const dashboardUrl = getDashboardUrl();

    // Show loading state - block until auth initialization is fully complete
    // This ensures no API calls happen with stale cookies during SSO consume
    if (loading || !ready) {
        return (
            <div className="h-dvh flex items-center justify-center bg-muted/50">
                <div className="text-center">
                    <Loader2 className="inline-block h-12 w-12 animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">{t('authGuard.authenticating')}</p>
                </div>
            </div>
        );
    }

    // Show error state — differentiate 403 (access denied) from other errors
    if (error) {
        const isForbidden = error instanceof AuthError && error.status === 403;

        if (isForbidden) {
            return <AccessDeniedScreen />;
        }

        return (
            <div className="h-dvh flex items-center justify-center bg-muted/50">
                <div className="max-w-md w-full mx-4">
                    <div className="bg-card rounded-lg shadow-lg p-6 text-center">
                        <div className="h-16 w-16 rounded-full bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center mx-auto mb-4">
                            <svg
                                className="h-8 w-8 text-amber-500 dark:text-amber-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12 20.5h.01"
                                />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-foreground mb-2">
                            {t('authGuard.connectionError.title')}
                        </h2>
                        <p className="text-muted-foreground mb-1">
                            {t('authGuard.connectionError.description')}
                        </p>
                        <p className="text-sm text-muted-foreground mb-6">
                            {t('authGuard.connectionError.hint')}
                        </p>
                        <Button onClick={() => window.location.reload()} className="w-full mb-3">
                            {t('authGuard.connectionError.reload')}
                        </Button>
                        {dashboardUrl && (
                            <Button variant="outline" asChild className="w-full">
                                <a href={dashboardUrl}>
                                    {t('authGuard.connectionError.backToDashboard')}
                                </a>
                            </Button>
                        )}
                        <p className="text-xs text-muted-foreground/70 mt-5">
                            {t('authGuard.connectionError.support')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Not authenticated — redirect to dashboard OAuth authorize
    if (!session?.authenticated) {
        if (onUnauthenticated) {
            onUnauthenticated();
        }

        if (fallback) {
            return <>{fallback}</>;
        }

        return <RedirectToLogin redirectedRef={redirectedRef} />;
    }

    // Authenticated but the identity has no operable WhatsApp line (explicit
    // backend verdict from the exchange / session status): every agent call
    // would be rejected, so show Access Denied instead of a broken inbox.
    // Undefined (older backend) renders normally.
    if (session.agentAccess === false) {
        return <AccessDeniedScreen />;
    }

    // Authenticated - render children
    return <>{children}</>;
}

function RedirectToLogin({ redirectedRef }: { redirectedRef: React.RefObject<boolean> }) {
    const { t } = useTranslation();
    const dashboardUrl = getDashboardUrl();

    useEffect(() => {
        if (redirectedRef.current) return;
        redirectedRef.current = true;

        if (dashboardUrl) {
            redirectToAuthorize();
        }
    }, [redirectedRef, dashboardUrl]);

    if (!dashboardUrl) {
        return (
            <div className="h-dvh flex items-center justify-center bg-muted/50">
                <div className="max-w-md w-full mx-4">
                    <div className="bg-card rounded-lg shadow-lg p-6 text-center">
                        <h2 className="text-xl font-semibold text-foreground mb-2">
                            {t('authGuard.configError.title')}
                        </h2>
                        <p className="text-muted-foreground">{t('authGuard.configError.description')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-dvh flex items-center justify-center bg-muted/50">
            <div className="text-center">
                <Loader2 className="inline-block h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">{t('authGuard.redirecting')}</p>
            </div>
        </div>
    );
}
